/* Mock data for Document Management - Franchise 65 (QuickStop Convenience) only */

import {
  DocumentCategory,
  DocumentPermission,
  DocumentScope,
  DocumentType,
  DocumentFolderDto,
  DocumentDto,
  DocumentVersionDto,
  FolderPermissionDto,
} from './documentsTypes';

/**
 * FOLDER STRUCTURE FOR TASK EVIDENCE:
 * 
 * The customer requested that evidence from tasks be stored in folders for organisation and ease of access.
 * 
 * RECOMMENDED: HYBRID STRUCTURE
 * 
 * Evidence is stored in a HYBRID structure combining both template-level and store-level organisation:
 * 
 * Structure:
 *   /[Franchise]/
 *     /Stores/
 *       /[Store Name]/
 *         /Task Evidence/
 *           /[Task Template Name]/
 *             /[Date - YYYY-MM-DD]/
 *               - evidence files...
 * 
 * Benefits of HYBRID approach:
 * 1. Store-level grouping: Store managers can easily access all evidence for their store
 * 2. Template organisation within store: Evidence is organised by task type for easy auditing
 * 3. Date-based sub-folders: Historical evidence is easily searchable by date
 * 4. Scalable: Works well for both single-store and multi-store franchises
 * 5. Permission inheritance: Store-level permissions naturally cascade to evidence folders
 */

export const mockFolders: DocumentFolderDto[] = [
  // ==========================================
  // FRANCHISE 65: QuickStop Convenience
  // ==========================================
  {
    id: 1,
    name: 'QuickStop Convenience',
    description: 'Root folder for QuickStop Convenience franchise',
    parentFolderId: null,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.General,
    franchiseId: 65,
    path: ['QuickStop Convenience'],
    childFolderCount: 4,
    documentCount: 0,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-01-01T10:00:00Z',
    createdByUserId: 1,
  },
  
  // --- Training Materials (Franchise-wide) ---
  {
    id: 2,
    name: 'Training Materials',
    description: 'Franchise-wide training documentation',
    parentFolderId: 1,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Training,
    franchiseId: 65,
    path: ['QuickStop Convenience', 'Training Materials'],
    childFolderCount: 3,
    documentCount: 2,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-01-15T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 3,
    name: 'New Employee Onboarding',
    description: 'Onboarding materials for new team members',
    parentFolderId: 2,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Training,
    franchiseId: 65,
    path: ['QuickStop Convenience', 'Training Materials', 'New Employee Onboarding'],
    childFolderCount: 0,
    documentCount: 5,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-01-16T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 4,
    name: 'Retail Safety Training',
    description: 'Retail safety and loss prevention training materials',
    parentFolderId: 2,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Training,
    franchiseId: 65,
    path: ['QuickStop Convenience', 'Training Materials', 'Retail Safety Training'],
    childFolderCount: 0,
    documentCount: 3,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-01-17T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 5,
    name: 'Equipment Guides',
    description: 'Operational guides for store equipment',
    parentFolderId: 2,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Operations,
    franchiseId: 65,
    path: ['QuickStop Convenience', 'Training Materials', 'Equipment Guides'],
    childFolderCount: 0,
    documentCount: 8,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-01-18T10:00:00Z',
    createdByUserId: 1,
  },
  
  // --- Policies & Procedures (Franchise-wide) ---
  {
    id: 6,
    name: 'Policies & Procedures',
    description: 'Company policies and standard procedures',
    parentFolderId: 1,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Policies,
    franchiseId: 65,
    path: ['QuickStop Convenience', 'Policies & Procedures'],
    childFolderCount: 3,
    documentCount: 4,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-02-01T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 7,
    name: 'HR Policies',
    description: 'Human resources policies',
    parentFolderId: 6,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Policies,
    franchiseId: 65,
    path: ['QuickStop Convenience', 'Policies & Procedures', 'HR Policies'],
    childFolderCount: 0,
    documentCount: 6,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-02-02T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 8,
    name: 'Health & Safety',
    description: 'Health and safety compliance documents',
    parentFolderId: 6,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Compliance,
    franchiseId: 65,
    path: ['QuickStop Convenience', 'Policies & Procedures', 'Health & Safety'],
    childFolderCount: 0,
    documentCount: 10,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-02-03T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 9,
    name: 'Operational Standards',
    description: 'Standard operating procedures and guidelines',
    parentFolderId: 6,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Operations,
    franchiseId: 65,
    path: ['QuickStop Convenience', 'Policies & Procedures', 'Operational Standards'],
    childFolderCount: 0,
    documentCount: 12,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-02-04T10:00:00Z',
    createdByUserId: 1,
  },
  
  // --- Stores Container (Franchise 65) ---
  {
    id: 10,
    name: 'Stores',
    description: 'Store-specific documents and task evidence',
    parentFolderId: 1,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.General,
    franchiseId: 65,
    path: ['QuickStop Convenience', 'Stores'],
    childFolderCount: 3,
    documentCount: 0,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-03-01T10:00:00Z',
    createdByUserId: 1,
  },
  
  // --- Store 001: Camden High Street ---
  {
    id: 11,
    name: 'Camden High Street',
    description: 'Store-specific documentation for Camden High Street',
    parentFolderId: 10,
    scope: DocumentScope.Store,
    category: DocumentCategory.General,
    franchiseId: 65,
    storeId: 1002804,
    path: ['QuickStop Convenience', 'Stores', 'Camden High Street'],
    childFolderCount: 2,
    documentCount: 0,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Upload, DocumentPermission.Download, DocumentPermission.Edit, DocumentPermission.Delete],
    createdAt: '2024-03-01T10:00:00Z',
    createdByUserId: 2,
  },
  {
    id: 12,
    name: 'Store Documents',
    description: 'General store documents and records',
    parentFolderId: 11,
    scope: DocumentScope.Store,
    category: DocumentCategory.General,
    franchiseId: 65,
    storeId: 1002804,
    path: ['QuickStop Convenience', 'Stores', 'Camden High Street', 'Store Documents'],
    childFolderCount: 0,
    documentCount: 4,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Upload, DocumentPermission.Download, DocumentPermission.Edit, DocumentPermission.Delete],
    createdAt: '2024-03-01T11:00:00Z',
    createdByUserId: 2,
  },
  {
    id: 13,
    name: 'Task Evidence',
    description: 'Evidence captured from completed tasks',
    parentFolderId: 11,
    scope: DocumentScope.TaskEvidence,
    category: DocumentCategory.Evidence,
    franchiseId: 65,
    storeId: 1002804,
    path: ['QuickStop Convenience', 'Stores', 'Camden High Street', 'Task Evidence'],
    childFolderCount: 3,
    documentCount: 0,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-03-02T10:00:00Z',
    createdByUserId: 2,
  },
  // Task Evidence by Template
  {
    id: 14,
    name: 'Opening Check',
    description: 'Evidence from Opening Check tasks',
    parentFolderId: 13,
    scope: DocumentScope.TaskEvidence,
    category: DocumentCategory.Evidence,
    franchiseId: 65,
    storeId: 1002804,
    taskTemplateId: 1,
    path: ['QuickStop Convenience', 'Stores', 'Camden High Street', 'Task Evidence', 'Opening Check'],
    childFolderCount: 2,
    documentCount: 0,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-03-02T10:00:00Z',
    createdByUserId: 2,
  },
  {
    id: 15,
    name: '2024-11-25',
    description: 'Evidence for Opening Check on 2024-11-25',
    parentFolderId: 14,
    scope: DocumentScope.TaskEvidence,
    category: DocumentCategory.Evidence,
    franchiseId: 65,
    storeId: 1002804,
    taskTemplateId: 1,
    taskInstanceId: 101,
    evidenceDate: '2024-11-25',
    path: ['QuickStop Convenience', 'Stores', 'Camden High Street', 'Task Evidence', 'Opening Check', '2024-11-25'],
    childFolderCount: 0,
    documentCount: 3,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-11-25T08:00:00Z',
    createdByUserId: 3,
  },
  {
    id: 16,
    name: '2024-11-26',
    description: 'Evidence for Opening Check on 2024-11-26',
    parentFolderId: 14,
    scope: DocumentScope.TaskEvidence,
    category: DocumentCategory.Evidence,
    franchiseId: 65,
    storeId: 1002804,
    taskTemplateId: 1,
    taskInstanceId: 105,
    evidenceDate: '2024-11-26',
    path: ['QuickStop Convenience', 'Stores', 'Camden High Street', 'Task Evidence', 'Opening Check', '2024-11-26'],
    childFolderCount: 0,
    documentCount: 2,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-11-26T08:00:00Z',
    createdByUserId: 3,
  },
  {
    id: 17,
    name: 'Temperature Monitoring',
    description: 'Evidence from Temperature Monitoring tasks',
    parentFolderId: 13,
    scope: DocumentScope.TaskEvidence,
    category: DocumentCategory.Evidence,
    franchiseId: 65,
    storeId: 1002804,
    taskTemplateId: 2,
    path: ['QuickStop Convenience', 'Stores', 'Camden High Street', 'Task Evidence', 'Temperature Monitoring'],
    childFolderCount: 3,
    documentCount: 0,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-03-03T10:00:00Z',
    createdByUserId: 2,
  },
  {
    id: 18,
    name: '2024-11-25',
    description: 'Evidence for Temperature Monitoring on 2024-11-25',
    parentFolderId: 17,
    scope: DocumentScope.TaskEvidence,
    category: DocumentCategory.Evidence,
    franchiseId: 65,
    storeId: 1002804,
    taskTemplateId: 2,
    taskInstanceId: 102,
    evidenceDate: '2024-11-25',
    path: ['QuickStop Convenience', 'Stores', 'Camden High Street', 'Task Evidence', 'Temperature Monitoring', '2024-11-25'],
    childFolderCount: 0,
    documentCount: 4,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-11-25T10:00:00Z',
    createdByUserId: 3,
  },
  {
    id: 19,
    name: 'Closing Check',
    description: 'Evidence from Closing Check tasks',
    parentFolderId: 13,
    scope: DocumentScope.TaskEvidence,
    category: DocumentCategory.Evidence,
    franchiseId: 65,
    storeId: 1002804,
    taskTemplateId: 3,
    path: ['QuickStop Convenience', 'Stores', 'Camden High Street', 'Task Evidence', 'Closing Check'],
    childFolderCount: 1,
    documentCount: 0,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-03-04T10:00:00Z',
    createdByUserId: 2,
  },
  {
    id: 20,
    name: '2024-11-25',
    description: 'Evidence for Closing Check on 2024-11-25',
    parentFolderId: 19,
    scope: DocumentScope.TaskEvidence,
    category: DocumentCategory.Evidence,
    franchiseId: 65,
    storeId: 1002804,
    taskTemplateId: 3,
    taskInstanceId: 103,
    evidenceDate: '2024-11-25',
    path: ['QuickStop Convenience', 'Stores', 'Camden High Street', 'Task Evidence', 'Closing Check', '2024-11-25'],
    childFolderCount: 0,
    documentCount: 2,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-11-25T22:00:00Z',
    createdByUserId: 3,
  },
  
  // --- Store 002: Shoreditch ---
  {
    id: 21,
    name: 'Shoreditch',
    description: 'Store-specific documentation for Shoreditch',
    parentFolderId: 10,
    scope: DocumentScope.Store,
    category: DocumentCategory.General,
    franchiseId: 65,
    storeId: 2,
    path: ['QuickStop Convenience', 'Stores', 'Shoreditch'],
    childFolderCount: 2,
    documentCount: 0,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Upload, DocumentPermission.Download, DocumentPermission.Edit, DocumentPermission.Delete],
    createdAt: '2024-03-05T10:00:00Z',
    createdByUserId: 4,
  },
  {
    id: 22,
    name: 'Store Documents',
    description: 'General store documents and records',
    parentFolderId: 21,
    scope: DocumentScope.Store,
    category: DocumentCategory.General,
    franchiseId: 65,
    storeId: 2,
    path: ['QuickStop Convenience', 'Stores', 'Shoreditch', 'Store Documents'],
    childFolderCount: 0,
    documentCount: 3,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Upload, DocumentPermission.Download, DocumentPermission.Edit, DocumentPermission.Delete],
    createdAt: '2024-03-05T11:00:00Z',
    createdByUserId: 4,
  },
  {
    id: 23,
    name: 'Task Evidence',
    description: 'Evidence captured from completed tasks',
    parentFolderId: 21,
    scope: DocumentScope.TaskEvidence,
    category: DocumentCategory.Evidence,
    franchiseId: 65,
    storeId: 2,
    path: ['QuickStop Convenience', 'Stores', 'Shoreditch', 'Task Evidence'],
    childFolderCount: 2,
    documentCount: 0,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-03-06T10:00:00Z',
    createdByUserId: 4,
  },
  {
    id: 24,
    name: 'Opening Check',
    description: 'Evidence from Opening Check tasks',
    parentFolderId: 23,
    scope: DocumentScope.TaskEvidence,
    category: DocumentCategory.Evidence,
    franchiseId: 65,
    storeId: 2,
    taskTemplateId: 1,
    path: ['QuickStop Convenience', 'Stores', 'Shoreditch', 'Task Evidence', 'Opening Check'],
    childFolderCount: 1,
    documentCount: 0,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-03-06T10:00:00Z',
    createdByUserId: 4,
  },
  {
    id: 25,
    name: '2024-11-25',
    description: 'Evidence for Opening Check on 2024-11-25',
    parentFolderId: 24,
    scope: DocumentScope.TaskEvidence,
    category: DocumentCategory.Evidence,
    franchiseId: 65,
    storeId: 2,
    taskTemplateId: 1,
    taskInstanceId: 201,
    evidenceDate: '2024-11-25',
    path: ['QuickStop Convenience', 'Stores', 'Shoreditch', 'Task Evidence', 'Opening Check', '2024-11-25'],
    childFolderCount: 0,
    documentCount: 2,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-11-25T08:30:00Z',
    createdByUserId: 5,
  },
  
  // --- Store 003: Bristol Temple Meads ---
  {
    id: 26,
    name: 'Bristol Temple Meads',
    description: 'Store-specific documentation for Bristol Temple Meads',
    parentFolderId: 10,
    scope: DocumentScope.Store,
    category: DocumentCategory.General,
    franchiseId: 65,
    storeId: 3,
    path: ['QuickStop Convenience', 'Stores', 'Bristol Temple Meads'],
    childFolderCount: 2,
    documentCount: 0,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Upload, DocumentPermission.Download, DocumentPermission.Edit, DocumentPermission.Delete],
    createdAt: '2024-03-07T10:00:00Z',
    createdByUserId: 6,
  },
  {
    id: 27,
    name: 'Store Documents',
    description: 'General store documents and records',
    parentFolderId: 26,
    scope: DocumentScope.Store,
    category: DocumentCategory.General,
    franchiseId: 65,
    storeId: 3,
    path: ['QuickStop Convenience', 'Stores', 'Bristol Temple Meads', 'Store Documents'],
    childFolderCount: 0,
    documentCount: 2,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Upload, DocumentPermission.Download, DocumentPermission.Edit, DocumentPermission.Delete],
    createdAt: '2024-03-07T11:00:00Z',
    createdByUserId: 6,
  },
  {
    id: 28,
    name: 'Task Evidence',
    description: 'Evidence captured from completed tasks',
    parentFolderId: 26,
    scope: DocumentScope.TaskEvidence,
    category: DocumentCategory.Evidence,
    franchiseId: 65,
    storeId: 3,
    path: ['QuickStop Convenience', 'Stores', 'Bristol Temple Meads', 'Task Evidence'],
    childFolderCount: 1,
    documentCount: 0,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-03-08T10:00:00Z',
    createdByUserId: 6,
  },
  
  // --- Compliance & Audits (Franchise-wide) ---
  {
    id: 29,
    name: 'Compliance & Audits',
    description: 'Compliance documentation and audit reports',
    parentFolderId: 1,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Compliance,
    franchiseId: 65,
    path: ['QuickStop Convenience', 'Compliance & Audits'],
    childFolderCount: 2,
    documentCount: 3,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-04-01T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 30,
    name: 'Retail Audits',
    description: 'Retail compliance audit reports and certificates',
    parentFolderId: 29,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Compliance,
    franchiseId: 65,
    path: ['QuickStop Convenience', 'Compliance & Audits', 'Retail Audits'],
    childFolderCount: 0,
    documentCount: 8,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-04-02T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 31,
    name: 'Environmental Health',
    description: 'Environmental health inspection reports',
    parentFolderId: 29,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Compliance,
    franchiseId: 65,
    path: ['QuickStop Convenience', 'Compliance & Audits', 'Environmental Health'],
    childFolderCount: 0,
    documentCount: 5,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-04-03T10:00:00Z',
    createdByUserId: 1,
  },

  // ==========================================
  // FRANCHISE 10: Vapiano
  // ==========================================
  {
    id: 300,
    name: 'Vapiano',
    description: 'Root folder for Vapiano franchise',
    parentFolderId: null,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.General,
    franchiseId: 10,
    path: ['Vapiano'],
    childFolderCount: 4,
    documentCount: 0,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-01-01T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 301,
    name: 'Training Materials',
    description: 'Training documentation for all Vapiano staff',
    parentFolderId: 300,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Training,
    franchiseId: 10,
    path: ['Vapiano', 'Training Materials'],
    childFolderCount: 4,
    documentCount: 0,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-01-10T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 302,
    name: 'Pasta Station Training',
    description: 'Fresh pasta preparation techniques and recipes',
    parentFolderId: 301,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Training,
    franchiseId: 10,
    path: ['Vapiano', 'Training Materials', 'Pasta Station Training'],
    childFolderCount: 0,
    documentCount: 6,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-01-11T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 303,
    name: 'Pizza Oven Training',
    description: 'Pizza oven operation, dough stretching, and topping standards',
    parentFolderId: 301,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Training,
    franchiseId: 10,
    path: ['Vapiano', 'Training Materials', 'Pizza Oven Training'],
    childFolderCount: 0,
    documentCount: 4,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-01-12T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 304,
    name: 'Bar & Cocktail Training',
    description: 'Espresso machine operation, cocktail recipes, Aperol Spritz preparation',
    parentFolderId: 301,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Training,
    franchiseId: 10,
    path: ['Vapiano', 'Training Materials', 'Bar & Cocktail Training'],
    childFolderCount: 0,
    documentCount: 5,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-01-13T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 305,
    name: 'Chip Card System Guide',
    description: 'How to use and troubleshoot the Vapiano chip card and NFC system',
    parentFolderId: 301,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Training,
    franchiseId: 10,
    path: ['Vapiano', 'Training Materials', 'Chip Card System Guide'],
    childFolderCount: 0,
    documentCount: 3,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-01-14T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 306,
    name: 'Recipes & Menu',
    description: 'Official Vapiano recipe cards and seasonal menu updates',
    parentFolderId: 300,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Operations,
    franchiseId: 10,
    path: ['Vapiano', 'Recipes & Menu'],
    childFolderCount: 3,
    documentCount: 2,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-02-01T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 307,
    name: 'Pasta Recipes',
    description: 'All pasta dish recipe cards with portioning and plating instructions',
    parentFolderId: 306,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Operations,
    franchiseId: 10,
    path: ['Vapiano', 'Recipes & Menu', 'Pasta Recipes'],
    childFolderCount: 0,
    documentCount: 18,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-02-02T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 308,
    name: 'Pizza Recipes',
    description: 'Pizza recipe cards, dough prep, and topping maps',
    parentFolderId: 306,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Operations,
    franchiseId: 10,
    path: ['Vapiano', 'Recipes & Menu', 'Pizza Recipes'],
    childFolderCount: 0,
    documentCount: 12,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-02-03T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 309,
    name: 'Seasonal Specials',
    description: 'Limited-time seasonal menu items and recipe cards',
    parentFolderId: 306,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Operations,
    franchiseId: 10,
    path: ['Vapiano', 'Recipes & Menu', 'Seasonal Specials'],
    childFolderCount: 0,
    documentCount: 4,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-02-04T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 310,
    name: 'Policies & Compliance',
    description: 'Vapiano operational policies, HACCP, and compliance docs',
    parentFolderId: 300,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Policies,
    franchiseId: 10,
    path: ['Vapiano', 'Policies & Compliance'],
    childFolderCount: 3,
    documentCount: 2,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-02-10T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 311,
    name: 'HACCP & Food Safety',
    description: 'HACCP plans, temperature log templates, and food safety guidelines',
    parentFolderId: 310,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Compliance,
    franchiseId: 10,
    path: ['Vapiano', 'Policies & Compliance', 'HACCP & Food Safety'],
    childFolderCount: 0,
    documentCount: 8,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-02-11T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 312,
    name: 'Open Kitchen Standards',
    description: 'Hygiene and presentation standards for the open kitchen concept',
    parentFolderId: 310,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Operations,
    franchiseId: 10,
    path: ['Vapiano', 'Policies & Compliance', 'Open Kitchen Standards'],
    childFolderCount: 0,
    documentCount: 5,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-02-12T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 313,
    name: 'Herb Garden Care Manual',
    description: 'Watering schedules, plant replacement procedures, and olive tree care',
    parentFolderId: 310,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Operations,
    franchiseId: 10,
    path: ['Vapiano', 'Policies & Compliance', 'Herb Garden Care Manual'],
    childFolderCount: 0,
    documentCount: 3,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-02-13T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 314,
    name: 'Stores',
    description: 'Store-specific documents and task evidence',
    parentFolderId: 300,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.General,
    franchiseId: 10,
    path: ['Vapiano', 'Stores'],
    childFolderCount: 3,
    documentCount: 0,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-03-01T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 315,
    name: 'Great Portland Street',
    description: 'Store documents for VAP001 - Great Portland Street',
    parentFolderId: 314,
    scope: DocumentScope.Store,
    category: DocumentCategory.General,
    franchiseId: 10,
    storeId: 1,
    path: ['Vapiano', 'Stores', 'Great Portland Street'],
    childFolderCount: 2,
    documentCount: 2,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Upload, DocumentPermission.Download, DocumentPermission.Edit],
    createdAt: '2024-03-02T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 316,
    name: 'Task Evidence',
    description: 'Evidence captured from completed tasks',
    parentFolderId: 315,
    scope: DocumentScope.TaskEvidence,
    category: DocumentCategory.Evidence,
    franchiseId: 10,
    storeId: 1,
    path: ['Vapiano', 'Stores', 'Great Portland Street', 'Task Evidence'],
    childFolderCount: 0,
    documentCount: 4,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-03-02T11:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 317,
    name: 'Store Documents',
    description: 'Local store notices and schedules',
    parentFolderId: 315,
    scope: DocumentScope.Store,
    category: DocumentCategory.General,
    franchiseId: 10,
    storeId: 1,
    path: ['Vapiano', 'Stores', 'Great Portland Street', 'Store Documents'],
    childFolderCount: 0,
    documentCount: 3,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Upload, DocumentPermission.Download, DocumentPermission.Edit],
    createdAt: '2024-03-02T12:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 318,
    name: 'Bankside',
    description: 'Store documents for VAP002 - Bankside',
    parentFolderId: 314,
    scope: DocumentScope.Store,
    category: DocumentCategory.General,
    franchiseId: 10,
    storeId: 2,
    path: ['Vapiano', 'Stores', 'Bankside'],
    childFolderCount: 1,
    documentCount: 1,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Upload, DocumentPermission.Download, DocumentPermission.Edit],
    createdAt: '2024-03-03T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 319,
    name: 'Manchester Deansgate',
    description: 'Store documents for VAP003 - Manchester Deansgate',
    parentFolderId: 314,
    scope: DocumentScope.Store,
    category: DocumentCategory.General,
    franchiseId: 10,
    storeId: 3,
    path: ['Vapiano', 'Stores', 'Manchester Deansgate'],
    childFolderCount: 1,
    documentCount: 1,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Upload, DocumentPermission.Download, DocumentPermission.Edit],
    createdAt: '2024-03-04T10:00:00Z',
    createdByUserId: 1,
  },

  // ==========================================
  // FRANCHISE 11: Five Guys
  // ==========================================
  {
    id: 400,
    name: 'Five Guys',
    description: 'Root folder for Five Guys franchise',
    parentFolderId: null,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.General,
    franchiseId: 11,
    path: ['Five Guys'],
    childFolderCount: 4,
    documentCount: 0,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-01-01T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 401,
    name: 'Training Materials',
    description: 'Training documentation for all Five Guys crew members',
    parentFolderId: 400,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Training,
    franchiseId: 11,
    path: ['Five Guys', 'Training Materials'],
    childFolderCount: 4,
    documentCount: 0,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-01-10T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 402,
    name: 'Grill & Burger Training',
    description: 'Patty cooking, smashing technique, temperature requirements, resting procedure',
    parentFolderId: 401,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Training,
    franchiseId: 11,
    path: ['Five Guys', 'Training Materials', 'Grill & Burger Training'],
    childFolderCount: 0,
    documentCount: 7,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-01-11T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 403,
    name: 'Fry Station Training',
    description: 'Double-cook method, potato selection, oil management, and the extra scoop',
    parentFolderId: 401,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Training,
    franchiseId: 11,
    path: ['Five Guys', 'Training Materials', 'Fry Station Training'],
    childFolderCount: 0,
    documentCount: 5,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-01-12T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 404,
    name: 'Peanut & Allergen Safety',
    description: 'Peanut allergen management, emergency protocols, signage requirements',
    parentFolderId: 401,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Training,
    franchiseId: 11,
    path: ['Five Guys', 'Training Materials', 'Peanut & Allergen Safety'],
    childFolderCount: 0,
    documentCount: 6,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-01-13T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 405,
    name: 'Milkshake & Mix-In Training',
    description: 'Shake preparation, mix-in portions, blender maintenance',
    parentFolderId: 401,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Training,
    franchiseId: 11,
    path: ['Five Guys', 'Training Materials', 'Milkshake & Mix-In Training'],
    childFolderCount: 0,
    documentCount: 4,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-01-14T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 406,
    name: 'Policies & Food Safety',
    description: 'Five Guys operational policies, food safety, and compliance documentation',
    parentFolderId: 400,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Policies,
    franchiseId: 11,
    path: ['Five Guys', 'Policies & Food Safety'],
    childFolderCount: 3,
    documentCount: 3,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-02-01T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 407,
    name: 'Allergen Management',
    description: 'Peanut oil policy, allergen matrices, FSA compliance documents',
    parentFolderId: 406,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Compliance,
    franchiseId: 11,
    path: ['Five Guys', 'Policies & Food Safety', 'Allergen Management'],
    childFolderCount: 0,
    documentCount: 10,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-02-02T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 408,
    name: 'Food Temperature Standards',
    description: 'Grill temps, hold times, oil temps, and cold storage requirements',
    parentFolderId: 406,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Compliance,
    franchiseId: 11,
    path: ['Five Guys', 'Policies & Food Safety', 'Food Temperature Standards'],
    childFolderCount: 0,
    documentCount: 5,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-02-03T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 409,
    name: 'Secret Shopper Programme',
    description: 'Secret shopper criteria, scoring guides, and historical results',
    parentFolderId: 406,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Operations,
    franchiseId: 11,
    path: ['Five Guys', 'Policies & Food Safety', 'Secret Shopper Programme'],
    childFolderCount: 0,
    documentCount: 8,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-02-04T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 410,
    name: 'Menu & Product Standards',
    description: 'Burger builds, topping portioning, and product specifications',
    parentFolderId: 400,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Operations,
    franchiseId: 11,
    path: ['Five Guys', 'Menu & Product Standards'],
    childFolderCount: 3,
    documentCount: 2,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-02-10T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 411,
    name: 'Burger Builds',
    description: 'Standard burger assembly guides — patty count, topping order, wrapping',
    parentFolderId: 410,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Operations,
    franchiseId: 11,
    path: ['Five Guys', 'Menu & Product Standards', 'Burger Builds'],
    childFolderCount: 0,
    documentCount: 8,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-02-11T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 412,
    name: 'Toppings & Freshness',
    description: 'Topping prep standards, freshness windows, hand-tear lettuce procedure',
    parentFolderId: 410,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Operations,
    franchiseId: 11,
    path: ['Five Guys', 'Menu & Product Standards', 'Toppings & Freshness'],
    childFolderCount: 0,
    documentCount: 6,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-02-12T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 413,
    name: 'Limited Time Offers',
    description: 'Current and upcoming LTO mix-ins and promotional items',
    parentFolderId: 410,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.Operations,
    franchiseId: 11,
    path: ['Five Guys', 'Menu & Product Standards', 'Limited Time Offers'],
    childFolderCount: 0,
    documentCount: 3,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-02-13T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 414,
    name: 'Stores',
    description: 'Store-specific documents and task evidence',
    parentFolderId: 400,
    scope: DocumentScope.Franchise,
    category: DocumentCategory.General,
    franchiseId: 11,
    path: ['Five Guys', 'Stores'],
    childFolderCount: 3,
    documentCount: 0,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-03-01T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 415,
    name: 'Covent Garden',
    description: 'Store documents for FG001 - Covent Garden (UK flagship)',
    parentFolderId: 414,
    scope: DocumentScope.Store,
    category: DocumentCategory.General,
    franchiseId: 11,
    storeId: 1,
    path: ['Five Guys', 'Stores', 'Covent Garden'],
    childFolderCount: 2,
    documentCount: 3,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Upload, DocumentPermission.Download, DocumentPermission.Edit],
    createdAt: '2024-03-02T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 416,
    name: 'Task Evidence',
    description: 'Evidence captured from completed tasks',
    parentFolderId: 415,
    scope: DocumentScope.TaskEvidence,
    category: DocumentCategory.Evidence,
    franchiseId: 11,
    storeId: 1,
    path: ['Five Guys', 'Stores', 'Covent Garden', 'Task Evidence'],
    childFolderCount: 0,
    documentCount: 6,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-03-02T11:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 417,
    name: 'Store Documents',
    description: 'Local store notices, rotas, and records',
    parentFolderId: 415,
    scope: DocumentScope.Store,
    category: DocumentCategory.General,
    franchiseId: 11,
    storeId: 1,
    path: ['Five Guys', 'Stores', 'Covent Garden', 'Store Documents'],
    childFolderCount: 0,
    documentCount: 4,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Upload, DocumentPermission.Download, DocumentPermission.Edit],
    createdAt: '2024-03-02T12:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 418,
    name: 'Birmingham Bullring',
    description: 'Store documents for FG002 - Birmingham Bullring',
    parentFolderId: 414,
    scope: DocumentScope.Store,
    category: DocumentCategory.General,
    franchiseId: 11,
    storeId: 2,
    path: ['Five Guys', 'Stores', 'Birmingham Bullring'],
    childFolderCount: 1,
    documentCount: 1,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Upload, DocumentPermission.Download, DocumentPermission.Edit],
    createdAt: '2024-03-03T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 419,
    name: 'Leeds Trinity',
    description: 'Store documents for FG003 - Leeds Trinity',
    parentFolderId: 414,
    scope: DocumentScope.Store,
    category: DocumentCategory.General,
    franchiseId: 11,
    storeId: 3,
    path: ['Five Guys', 'Stores', 'Leeds Trinity'],
    childFolderCount: 1,
    documentCount: 1,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Upload, DocumentPermission.Download, DocumentPermission.Edit],
    createdAt: '2024-03-04T10:00:00Z',
    createdByUserId: 1,
  },

  // ==========================================
  // SYSTEM-WIDE: Shared Resources
  // ==========================================
  {
    id: 200,
    name: 'System Resources',
    description: 'System-wide shared resources available to all franchises',
    parentFolderId: null,
    scope: DocumentScope.System,
    category: DocumentCategory.General,
    path: ['System Resources'],
    childFolderCount: 3,
    documentCount: 0,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-01-01T09:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 201,
    name: 'Brand Guidelines',
    description: 'Corporate branding and marketing materials',
    parentFolderId: 200,
    scope: DocumentScope.System,
    category: DocumentCategory.Marketing,
    path: ['System Resources', 'Brand Guidelines'],
    childFolderCount: 0,
    documentCount: 6,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-01-02T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 202,
    name: 'Legal Templates',
    description: 'Standard legal document templates',
    parentFolderId: 200,
    scope: DocumentScope.System,
    category: DocumentCategory.Compliance,
    path: ['System Resources', 'Legal Templates'],
    childFolderCount: 0,
    documentCount: 8,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-01-03T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 203,
    name: 'Best Practices',
    description: 'Industry best practices and guidelines',
    parentFolderId: 200,
    scope: DocumentScope.System,
    category: DocumentCategory.Training,
    path: ['System Resources', 'Best Practices'],
    childFolderCount: 0,
    documentCount: 10,
    effectivePermissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-01-04T10:00:00Z',
    createdByUserId: 1,
  },
];

// Document versions - each document can have multiple versions
export const mockDocumentVersions: DocumentVersionDto[] = [
  // Document 1: Welcome Guide (1 version)
  { id: 1, documentId: 1, versionNumber: 1, fileName: 'welcome_guide.pdf', fileSize: 2456789, mimeType: 'application/pdf', documentType: DocumentType.PDF, blobUrl: '/mock-documents/welcome_guide.pdf', thumbnailUrl: '/mock-documents/thumbnails/welcome_guide.png', changeNotes: 'Initial upload', createdAt: '2024-01-15T11:00:00Z', createdByUserId: 1, createdByUserName: 'Admin User' },
  
  // Document 2: Day 1 Checklist (2 versions)
  { id: 2, documentId: 2, versionNumber: 1, fileName: 'day1_checklist_v1.pdf', fileSize: 500000, mimeType: 'application/pdf', documentType: DocumentType.PDF, blobUrl: '/mock-documents/day1_checklist_v1.pdf', changeNotes: 'Initial upload', createdAt: '2024-01-16T12:00:00Z', createdByUserId: 1, createdByUserName: 'Admin User' },
  { id: 3, documentId: 2, versionNumber: 2, fileName: 'day1_checklist.pdf', fileSize: 543210, mimeType: 'application/pdf', documentType: DocumentType.PDF, blobUrl: '/mock-documents/day1_checklist.pdf', changeNotes: 'Updated checklist items', createdAt: '2024-02-01T09:00:00Z', createdByUserId: 1, createdByUserName: 'Admin User' },
  
  // Document 3: Retail Safety Fundamentals (3 versions)
  { id: 4, documentId: 3, versionNumber: 1, fileName: 'retail_safety_v1.pptx', fileSize: 7000000, mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', documentType: DocumentType.Presentation, blobUrl: '/mock-documents/retail_safety_v1.pptx', changeNotes: 'Initial upload', createdAt: '2024-01-17T14:00:00Z', createdByUserId: 1, createdByUserName: 'Admin User' },
  { id: 5, documentId: 3, versionNumber: 2, fileName: 'retail_safety_v2.pptx', fileSize: 7500000, mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', documentType: DocumentType.Presentation, blobUrl: '/mock-documents/retail_safety_v2.pptx', changeNotes: 'Added loss prevention section', createdAt: '2024-03-15T10:00:00Z', createdByUserId: 1, createdByUserName: 'Admin User' },
  { id: 6, documentId: 3, versionNumber: 3, fileName: 'retail_safety_fundamentals.pptx', fileSize: 8765432, mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', documentType: DocumentType.Presentation, blobUrl: '/mock-documents/retail_safety_fundamentals.pptx', changeNotes: 'Updated with 2024 regulations', createdAt: '2024-06-01T10:00:00Z', createdByUserId: 1, createdByUserName: 'Admin User' },
  
  // Document 4: POS Terminal Manual (1 version)
  { id: 7, documentId: 4, versionNumber: 1, fileName: 'pos_terminal_manual.pdf', fileSize: 3456789, mimeType: 'application/pdf', documentType: DocumentType.PDF, blobUrl: '/mock-documents/pos_terminal_manual.pdf', thumbnailUrl: '/mock-documents/thumbnails/pos_terminal.png', changeNotes: 'Initial upload', createdAt: '2024-01-18T10:30:00Z', createdByUserId: 1, createdByUserName: 'Admin User' },
  
  // Document 5-17: Task evidence (1 version each)
  { id: 8, documentId: 5, versionNumber: 1, fileName: 'temp_check_001.jpg', fileSize: 456789, mimeType: 'image/jpeg', documentType: DocumentType.Image, blobUrl: '/mock-documents/evidence/temp_check_001.jpg', thumbnailUrl: '/mock-documents/evidence/temp_check_001_thumb.jpg', createdAt: '2024-11-25T08:15:00Z', createdByUserId: 3, createdByUserName: 'Store Manager' },
  { id: 9, documentId: 6, versionNumber: 1, fileName: 'signature_001.png', fileSize: 23456, mimeType: 'image/png', documentType: DocumentType.Image, blobUrl: '/mock-documents/evidence/signature_001.png', createdAt: '2024-11-25T08:20:00Z', createdByUserId: 3, createdByUserName: 'Store Manager' },
  { id: 10, documentId: 7, versionNumber: 1, fileName: 'till_float_001.jpg', fileSize: 345678, mimeType: 'image/jpeg', documentType: DocumentType.Image, blobUrl: '/mock-documents/evidence/till_float_001.jpg', createdAt: '2024-11-25T08:25:00Z', createdByUserId: 3, createdByUserName: 'Store Manager' },
  { id: 11, documentId: 8, versionNumber: 1, fileName: 'fridge_temp_0800.jpg', fileSize: 234567, mimeType: 'image/jpeg', documentType: DocumentType.Image, blobUrl: '/mock-documents/evidence/fridge_temp_0800.jpg', createdAt: '2024-11-25T08:00:00Z', createdByUserId: 3, createdByUserName: 'Store Manager' },
  { id: 12, documentId: 9, versionNumber: 1, fileName: 'fridge_temp_1200.jpg', fileSize: 234567, mimeType: 'image/jpeg', documentType: DocumentType.Image, blobUrl: '/mock-documents/evidence/fridge_temp_1200.jpg', createdAt: '2024-11-25T12:00:00Z', createdByUserId: 3, createdByUserName: 'Team Member' },
  { id: 13, documentId: 10, versionNumber: 1, fileName: 'freezer_temp_0800.jpg', fileSize: 234567, mimeType: 'image/jpeg', documentType: DocumentType.Image, blobUrl: '/mock-documents/evidence/freezer_temp_0800.jpg', createdAt: '2024-11-25T08:05:00Z', createdByUserId: 3, createdByUserName: 'Store Manager' },
  { id: 14, documentId: 11, versionNumber: 1, fileName: 'hothold_temp_001.jpg', fileSize: 234567, mimeType: 'image/jpeg', documentType: DocumentType.Image, blobUrl: '/mock-documents/evidence/hothold_temp_001.jpg', createdAt: '2024-11-25T11:30:00Z', createdByUserId: 3, createdByUserName: 'Team Member' },
  { id: 15, documentId: 12, versionNumber: 1, fileName: 'till_report_20241125.pdf', fileSize: 123456, mimeType: 'application/pdf', documentType: DocumentType.PDF, blobUrl: '/mock-documents/evidence/till_report_20241125.pdf', createdAt: '2024-11-25T22:00:00Z', createdByUserId: 3, createdByUserName: 'Store Manager' },
  { id: 16, documentId: 13, versionNumber: 1, fileName: 'closing_signature_001.png', fileSize: 23456, mimeType: 'image/png', documentType: DocumentType.Image, blobUrl: '/mock-documents/evidence/closing_signature_001.png', createdAt: '2024-11-25T22:05:00Z', createdByUserId: 3, createdByUserName: 'Store Manager' },
  { id: 17, documentId: 14, versionNumber: 1, fileName: 'temp_check_002.jpg', fileSize: 456789, mimeType: 'image/jpeg', documentType: DocumentType.Image, blobUrl: '/mock-documents/evidence/temp_check_002.jpg', createdAt: '2024-11-26T08:10:00Z', createdByUserId: 3, createdByUserName: 'Store Manager' },
  { id: 18, documentId: 15, versionNumber: 1, fileName: 'signature_002.png', fileSize: 23456, mimeType: 'image/png', documentType: DocumentType.Image, blobUrl: '/mock-documents/evidence/signature_002.png', createdAt: '2024-11-26T08:15:00Z', createdByUserId: 3, createdByUserName: 'Store Manager' },
  { id: 19, documentId: 16, versionNumber: 1, fileName: 'shoreditch_temp_check_001.jpg', fileSize: 456789, mimeType: 'image/jpeg', documentType: DocumentType.Image, blobUrl: '/mock-documents/evidence/shoreditch_temp_check_001.jpg', createdAt: '2024-11-25T08:30:00Z', createdByUserId: 5, createdByUserName: 'Shoreditch Manager' },
  { id: 20, documentId: 17, versionNumber: 1, fileName: 'shoreditch_signature_001.png', fileSize: 23456, mimeType: 'image/png', documentType: DocumentType.Image, blobUrl: '/mock-documents/evidence/shoreditch_signature_001.png', createdAt: '2024-11-25T08:35:00Z', createdByUserId: 5, createdByUserName: 'Shoreditch Manager' },
  
  // Document 18: Employee Handbook (4 versions)
  { id: 21, documentId: 18, versionNumber: 1, fileName: 'employee_handbook_2024_v1.pdf', fileSize: 4500000, mimeType: 'application/pdf', documentType: DocumentType.PDF, blobUrl: '/mock-documents/policies/employee_handbook_2024_v1.pdf', changeNotes: 'Initial 2024 handbook', createdAt: '2024-01-01T10:00:00Z', createdByUserId: 1, createdByUserName: 'Admin User' },
  { id: 22, documentId: 18, versionNumber: 2, fileName: 'employee_handbook_2024_v2.pdf', fileSize: 4800000, mimeType: 'application/pdf', documentType: DocumentType.PDF, blobUrl: '/mock-documents/policies/employee_handbook_2024_v2.pdf', changeNotes: 'Added remote work policy', createdAt: '2024-02-15T10:00:00Z', createdByUserId: 1, createdByUserName: 'Admin User' },
  { id: 23, documentId: 18, versionNumber: 3, fileName: 'employee_handbook_2024_v3.pdf', fileSize: 5200000, mimeType: 'application/pdf', documentType: DocumentType.PDF, blobUrl: '/mock-documents/policies/employee_handbook_2024_v3.pdf', changeNotes: 'Updated leave policies', createdAt: '2024-04-01T10:00:00Z', createdByUserId: 1, createdByUserName: 'Admin User' },
  { id: 24, documentId: 18, versionNumber: 4, fileName: 'employee_handbook_2024.pdf', fileSize: 5678901, mimeType: 'application/pdf', documentType: DocumentType.PDF, blobUrl: '/mock-documents/policies/employee_handbook_2024.pdf', changeNotes: 'Mid-year review updates', createdAt: '2024-06-01T10:00:00Z', createdByUserId: 1, createdByUserName: 'Admin User' },
  
  // Document 19: Disciplinary Procedure (2 versions)
  { id: 25, documentId: 19, versionNumber: 1, fileName: 'disciplinary_procedure_v1.pdf', fileSize: 200000, mimeType: 'application/pdf', documentType: DocumentType.PDF, blobUrl: '/mock-documents/policies/disciplinary_procedure_v1.pdf', changeNotes: 'Initial upload', createdAt: '2024-01-02T10:00:00Z', createdByUserId: 1, createdByUserName: 'Admin User' },
  { id: 26, documentId: 19, versionNumber: 2, fileName: 'disciplinary_procedure.pdf', fileSize: 234567, mimeType: 'application/pdf', documentType: DocumentType.PDF, blobUrl: '/mock-documents/policies/disciplinary_procedure.pdf', changeNotes: 'Updated appeal process', createdAt: '2024-05-01T10:00:00Z', createdByUserId: 1, createdByUserName: 'Admin User' },
  
  // Document 20: Fire Safety Plan (3 versions)
  { id: 27, documentId: 20, versionNumber: 1, fileName: 'fire_safety_plan_v1.pdf', fileSize: 700000, mimeType: 'application/pdf', documentType: DocumentType.PDF, blobUrl: '/mock-documents/policies/fire_safety_plan_v1.pdf', changeNotes: 'Initial upload', createdAt: '2024-02-01T10:00:00Z', createdByUserId: 1, createdByUserName: 'Admin User' },
  { id: 28, documentId: 20, versionNumber: 2, fileName: 'fire_safety_plan_v2.pdf', fileSize: 800000, mimeType: 'application/pdf', documentType: DocumentType.PDF, blobUrl: '/mock-documents/policies/fire_safety_plan_v2.pdf', changeNotes: 'Added evacuation maps', createdAt: '2024-04-15T10:00:00Z', createdByUserId: 1, createdByUserName: 'Admin User' },
  { id: 29, documentId: 20, versionNumber: 3, fileName: 'fire_safety_plan.pdf', fileSize: 876543, mimeType: 'application/pdf', documentType: DocumentType.PDF, blobUrl: '/mock-documents/policies/fire_safety_plan.pdf', changeNotes: 'Updated assembly points', createdAt: '2024-08-01T10:00:00Z', createdByUserId: 1, createdByUserName: 'Admin User' },
  
  // Document 21-22: Store-specific (1-2 versions)
  { id: 30, documentId: 21, versionNumber: 1, fileName: 'camden_layout.pdf', fileSize: 1234567, mimeType: 'application/pdf', documentType: DocumentType.PDF, blobUrl: '/mock-documents/stores/camden_layout.pdf', changeNotes: 'Initial store layout', createdAt: '2024-03-01T12:00:00Z', createdByUserId: 2, createdByUserName: 'Camden Manager' },
  { id: 31, documentId: 22, versionNumber: 1, fileName: 'emergency_contacts_camden_v1.xlsx', fileSize: 40000, mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', documentType: DocumentType.Spreadsheet, blobUrl: '/mock-documents/stores/emergency_contacts_camden_v1.xlsx', changeNotes: 'Initial contact list', createdAt: '2024-03-01T12:30:00Z', createdByUserId: 2, createdByUserName: 'Camden Manager' },
  { id: 32, documentId: 22, versionNumber: 2, fileName: 'emergency_contacts_camden.xlsx', fileSize: 45678, mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', documentType: DocumentType.Spreadsheet, blobUrl: '/mock-documents/stores/emergency_contacts_camden.xlsx', changeNotes: 'Updated emergency services numbers', createdAt: '2024-09-15T10:00:00Z', createdByUserId: 2, createdByUserName: 'Camden Manager' },
  
  // Document 23-24: System resources
  { id: 33, documentId: 23, versionNumber: 1, fileName: 'logo_guidelines.pdf', fileSize: 3456789, mimeType: 'application/pdf', documentType: DocumentType.PDF, blobUrl: '/mock-documents/system/logo_guidelines.pdf', changeNotes: 'Initial brand guidelines', createdAt: '2024-01-02T11:00:00Z', createdByUserId: 1, createdByUserName: 'System Admin' },
  { id: 34, documentId: 24, versionNumber: 1, fileName: 'nda_template_v1.docx', fileSize: 75000, mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', documentType: DocumentType.Document, blobUrl: '/mock-documents/system/nda_template_v1.docx', changeNotes: 'Initial template', createdAt: '2024-01-03T11:00:00Z', createdByUserId: 1, createdByUserName: 'System Admin' },
  { id: 35, documentId: 24, versionNumber: 2, fileName: 'nda_template.docx', fileSize: 87654, mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', documentType: DocumentType.Document, blobUrl: '/mock-documents/system/nda_template.docx', changeNotes: 'Updated legal clauses', createdAt: '2024-06-15T11:00:00Z', createdByUserId: 1, createdByUserName: 'System Admin' },
];

// Helper to get the latest version for a document
const getLatestVersion = (documentId: number): DocumentVersionDto => {
  const versions = mockDocumentVersions.filter(v => v.documentId === documentId);
  return versions.reduce((latest, v) => v.versionNumber > latest.versionNumber ? v : latest, versions[0]);
};

export const mockDocuments: DocumentDto[] = [
  // Training Materials documents
  {
    id: 1,
    folderId: 2,
    name: 'Welcome Guide',
    description: 'Welcome guide for all employees',
    tags: ['onboarding', 'welcome', 'introduction'],
    linkedTaskTemplateIds: [],
    versionCount: 1,
    latestVersion: getLatestVersion(1),
    createdAt: '2024-01-15T11:00:00Z',
    createdByUserId: 1,
    createdByUserName: 'Admin User',
  },
  {
    id: 2,
    folderId: 3,
    name: 'Day 1 Checklist',
    description: 'Checklist for new employee first day',
    tags: ['onboarding', 'checklist', 'first-day'],
    linkedTaskTemplateIds: [1],
    versionCount: 2,
    latestVersion: getLatestVersion(2),
    createdAt: '2024-01-16T12:00:00Z',
    createdByUserId: 1,
    createdByUserName: 'Admin User',
    updatedAt: '2024-02-01T09:00:00Z',
    updatedByUserId: 1,
  },
  {
    id: 3,
    folderId: 4,
    name: 'Retail Safety Fundamentals',
    description: 'Core retail safety and loss prevention training presentation',
    tags: ['training', 'retail-safety', 'loss-prevention'],
    linkedTaskTemplateIds: [2, 5],
    versionCount: 3,
    latestVersion: getLatestVersion(3),
    createdAt: '2024-01-17T14:00:00Z',
    createdByUserId: 1,
    createdByUserName: 'Admin User',
  },
  {
    id: 4,
    folderId: 5,
    name: 'POS Terminal Manual',
    description: 'Operating manual for the point of sale terminal',
    tags: ['equipment', 'pos', 'manual'],
    linkedTaskTemplateIds: [3],
    versionCount: 1,
    latestVersion: getLatestVersion(4),
    createdAt: '2024-01-18T10:30:00Z',
    createdByUserId: 1,
    createdByUserName: 'Admin User',
  },
  
  // Task Evidence - Camden High Street Opening Check 2024-11-25
  {
    id: 5,
    folderId: 15,
    name: 'Temperature Check Photo',
    description: 'Photo evidence of temperature reading',
    tags: ['evidence', 'temperature', 'opening-check'],
    versionCount: 1,
    latestVersion: getLatestVersion(5),
    createdAt: '2024-11-25T08:15:00Z',
    createdByUserId: 3,
    createdByUserName: 'Store Manager',
  },
  {
    id: 6,
    folderId: 15,
    name: 'Manager Signature',
    description: 'Manager signature for opening check completion',
    tags: ['evidence', 'signature', 'opening-check'],
    versionCount: 1,
    latestVersion: getLatestVersion(6),
    createdAt: '2024-11-25T08:20:00Z',
    createdByUserId: 3,
    createdByUserName: 'Store Manager',
  },
  {
    id: 7,
    folderId: 15,
    name: 'Till Float Verification',
    description: 'Photo of till float count',
    tags: ['evidence', 'till', 'opening-check'],
    versionCount: 1,
    latestVersion: getLatestVersion(7),
    createdAt: '2024-11-25T08:25:00Z',
    createdByUserId: 3,
    createdByUserName: 'Store Manager',
  },
  
  // Task Evidence - Camden High Street Temperature Monitoring 2024-11-25
  {
    id: 8,
    folderId: 18,
    name: 'Fridge Temperature - 08:00',
    description: 'Morning fridge temperature reading',
    tags: ['evidence', 'temperature', 'fridge'],
    versionCount: 1,
    latestVersion: getLatestVersion(8),
    createdAt: '2024-11-25T08:00:00Z',
    createdByUserId: 3,
    createdByUserName: 'Store Manager',
  },
  {
    id: 9,
    folderId: 18,
    name: 'Fridge Temperature - 12:00',
    description: 'Midday fridge temperature reading',
    tags: ['evidence', 'temperature', 'fridge'],
    versionCount: 1,
    latestVersion: getLatestVersion(9),
    createdAt: '2024-11-25T12:00:00Z',
    createdByUserId: 3,
    createdByUserName: 'Team Member',
  },
  {
    id: 10,
    folderId: 18,
    name: 'Freezer Temperature - 08:00',
    description: 'Morning freezer temperature reading',
    tags: ['evidence', 'temperature', 'freezer'],
    versionCount: 1,
    latestVersion: getLatestVersion(10),
    createdAt: '2024-11-25T08:05:00Z',
    createdByUserId: 3,
    createdByUserName: 'Store Manager',
  },
  {
    id: 11,
    folderId: 18,
    name: 'Hot Hold Temperature Check',
    description: 'Hot hold unit temperature verification',
    tags: ['evidence', 'temperature', 'hot-hold'],
    versionCount: 1,
    latestVersion: getLatestVersion(11),
    createdAt: '2024-11-25T11:30:00Z',
    createdByUserId: 3,
    createdByUserName: 'Team Member',
  },
  
  // Task Evidence - Camden High Street Closing Check 2024-11-25
  {
    id: 12,
    folderId: 20,
    name: 'End of Day Till Report',
    description: 'Till reconciliation report for closing',
    tags: ['evidence', 'till', 'closing-check'],
    versionCount: 1,
    latestVersion: getLatestVersion(12),
    createdAt: '2024-11-25T22:00:00Z',
    createdByUserId: 3,
    createdByUserName: 'Store Manager',
  },
  {
    id: 13,
    folderId: 20,
    name: 'Closing Manager Signature',
    description: 'Manager signature confirming store closure',
    tags: ['evidence', 'signature', 'closing-check'],
    versionCount: 1,
    latestVersion: getLatestVersion(13),
    createdAt: '2024-11-25T22:05:00Z',
    createdByUserId: 3,
    createdByUserName: 'Store Manager',
  },
  
  // Task Evidence - Camden High Street Opening Check 2024-11-26
  {
    id: 14,
    folderId: 16,
    name: 'Temperature Check Photo',
    description: 'Photo evidence of temperature reading',
    tags: ['evidence', 'temperature', 'opening-check'],
    versionCount: 1,
    latestVersion: getLatestVersion(14),
    createdAt: '2024-11-26T08:10:00Z',
    createdByUserId: 3,
    createdByUserName: 'Store Manager',
  },
  {
    id: 15,
    folderId: 16,
    name: 'Manager Signature',
    description: 'Manager signature for opening check completion',
    tags: ['evidence', 'signature', 'opening-check'],
    versionCount: 1,
    latestVersion: getLatestVersion(15),
    createdAt: '2024-11-26T08:15:00Z',
    createdByUserId: 3,
    createdByUserName: 'Store Manager',
  },
  
  // Task Evidence - Shoreditch Opening Check 2024-11-25
  {
    id: 16,
    folderId: 25,
    name: 'Temperature Check Photo',
    description: 'Photo evidence of temperature reading',
    tags: ['evidence', 'temperature', 'opening-check'],
    versionCount: 1,
    latestVersion: getLatestVersion(16),
    createdAt: '2024-11-25T08:30:00Z',
    createdByUserId: 5,
    createdByUserName: 'Shoreditch Manager',
  },
  {
    id: 17,
    folderId: 25,
    name: 'Manager Signature',
    description: 'Manager signature for opening check completion',
    tags: ['evidence', 'signature', 'opening-check'],
    versionCount: 1,
    latestVersion: getLatestVersion(17),
    createdAt: '2024-11-25T08:35:00Z',
    createdByUserId: 5,
    createdByUserName: 'Shoreditch Manager',
  },
  
  // Policy documents
  {
    id: 18,
    folderId: 7,
    name: 'Employee Handbook 2024',
    description: 'Complete employee handbook for all staff',
    tags: ['hr', 'handbook', 'policies'],
    versionCount: 4,
    latestVersion: getLatestVersion(18),
    createdAt: '2024-01-01T10:00:00Z',
    createdByUserId: 1,
    createdByUserName: 'Admin User',
    updatedAt: '2024-06-01T10:00:00Z',
    updatedByUserId: 1,
  },
  {
    id: 19,
    folderId: 7,
    name: 'Disciplinary Procedure',
    description: 'Formal disciplinary procedure guidelines',
    tags: ['hr', 'disciplinary', 'procedure'],
    versionCount: 2,
    latestVersion: getLatestVersion(19),
    createdAt: '2024-01-02T10:00:00Z',
    createdByUserId: 1,
    createdByUserName: 'Admin User',
  },
  {
    id: 20,
    folderId: 8,
    name: 'Fire Safety Plan',
    description: 'Store fire safety and evacuation procedures',
    tags: ['safety', 'fire', 'evacuation'],
    versionCount: 3,
    latestVersion: getLatestVersion(20),
    createdAt: '2024-02-01T10:00:00Z',
    createdByUserId: 1,
    createdByUserName: 'Admin User',
  },
  
  // Store-specific documents
  {
    id: 21,
    folderId: 12,
    name: 'Store Layout Plan',
    description: 'Floor plan and layout for Camden High Street store',
    tags: ['store', 'layout', 'camden'],
    versionCount: 1,
    latestVersion: getLatestVersion(21),
    createdAt: '2024-03-01T12:00:00Z',
    createdByUserId: 2,
    createdByUserName: 'Camden Manager',
  },
  {
    id: 22,
    folderId: 12,
    name: 'Emergency Contact List',
    description: 'Emergency contacts for Camden High Street',
    tags: ['emergency', 'contacts', 'camden'],
    versionCount: 2,
    latestVersion: getLatestVersion(22),
    createdAt: '2024-03-01T12:30:00Z',
    createdByUserId: 2,
    createdByUserName: 'Camden Manager',
    updatedAt: '2024-09-15T10:00:00Z',
    updatedByUserId: 2,
  },
  
  // System resources
  {
    id: 23,
    folderId: 201,
    name: 'Logo Usage Guidelines',
    description: 'Official guidelines for logo usage',
    tags: ['brand', 'logo', 'guidelines'],
    versionCount: 1,
    latestVersion: getLatestVersion(23),
    createdAt: '2024-01-02T11:00:00Z',
    createdByUserId: 1,
    createdByUserName: 'System Admin',
  },
  {
    id: 24,
    folderId: 202,
    name: 'Non-Disclosure Agreement Template',
    description: 'Standard NDA template for partners',
    tags: ['legal', 'nda', 'template'],
    versionCount: 2,
    latestVersion: getLatestVersion(24),
    createdAt: '2024-01-03T11:00:00Z',
    createdByUserId: 1,
    createdByUserName: 'System Admin',
  },
  // CCTV Footage for Sales Transactions
  {
    id: 1001,
    folderId: 12,
    name: 'CCTV - POS Transaction 20251212-001',
    description: 'CCTV footage for Milk sale on 2025-12-12 at 10:30',
    tags: ['cctv', 'transaction', 'sales', 'pos'],
    versionCount: 1,
    latestVersion: {
      id: 1001,
      documentId: 1001,
      versionNumber: 1,
      fileName: 'cctv_20251212_1030.mp4',
      fileSize: 25600000,
      mimeType: 'video/mp4',
      documentType: DocumentType.Video,
      blobUrl: '/mock-documents/cctv/cctv_20251212_1030.mp4',
      createdAt: '2025-12-12T10:30:00Z',
      createdByUserId: 1,
      createdByUserName: 'System',
    },
    createdAt: '2025-12-12T10:30:00Z',
    createdByUserId: 1,
    createdByUserName: 'System',
  },
  {
    id: 1002,
    folderId: 12,
    name: 'CCTV - POS Transaction 20251211-023',
    description: 'CCTV footage for Coffee sale on 2025-12-11 at 15:45',
    tags: ['cctv', 'transaction', 'sales', 'pos'],
    versionCount: 1,
    latestVersion: {
      id: 1002,
      documentId: 1002,
      versionNumber: 1,
      fileName: 'cctv_20251211_1545.mp4',
      fileSize: 28900000,
      mimeType: 'video/mp4',
      documentType: DocumentType.Video,
      blobUrl: '/mock-documents/cctv/cctv_20251211_1545.mp4',
      createdAt: '2025-12-11T15:45:00Z',
      createdByUserId: 1,
      createdByUserName: 'System',
    },
    createdAt: '2025-12-11T15:45:00Z',
    createdByUserId: 1,
    createdByUserName: 'System',
  },
  {
    id: 1003,
    folderId: 12,
    name: 'CCTV - POS Transaction 20251210-087',
    description: 'CCTV footage for Sandwich sale on 2025-12-10 at 12:30',
    tags: ['cctv', 'transaction', 'sales', 'pos'],
    versionCount: 1,
    latestVersion: {
      id: 1003,
      documentId: 1003,
      versionNumber: 1,
      fileName: 'cctv_20251210_1230.mp4',
      fileSize: 31200000,
      mimeType: 'video/mp4',
      documentType: DocumentType.Video,
      blobUrl: '/mock-documents/cctv/cctv_20251210_1230.mp4',
      createdAt: '2025-12-10T12:30:00Z',
      createdByUserId: 1,
      createdByUserName: 'System',
    },
    createdAt: '2025-12-10T12:30:00Z',
    createdByUserId: 1,
    createdByUserName: 'System',
  },
  {
    id: 1004,
    folderId: 12,
    name: 'CCTV - POS Transaction 20251209-045',
    description: 'CCTV footage for Pasta sale on 2025-12-09 at 13:15',
    tags: ['cctv', 'transaction', 'sales', 'pos'],
    versionCount: 1,
    latestVersion: {
      id: 1004,
      documentId: 1004,
      versionNumber: 1,
      fileName: 'cctv_20251209_1315.mp4',
      fileSize: 27400000,
      mimeType: 'video/mp4',
      documentType: DocumentType.Video,
      blobUrl: '/mock-documents/cctv/cctv_20251209_1315.mp4',
      createdAt: '2025-12-09T13:15:00Z',
      createdByUserId: 1,
      createdByUserName: 'System',
    },
    createdAt: '2025-12-09T13:15:00Z',
    createdByUserId: 1,
    createdByUserName: 'System',
  },
];

export const mockPermissions: FolderPermissionDto[] = [
  {
    id: 1,
    folderId: 2,
    roleId: 1, // Store Manager role
    permissions: [DocumentPermission.View, DocumentPermission.Download, DocumentPermission.Upload],
    createdAt: '2024-01-15T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 2,
    folderId: 2,
    roleId: 2, // Team Member role
    permissions: [DocumentPermission.View, DocumentPermission.Download],
    createdAt: '2024-01-15T10:00:00Z',
    createdByUserId: 1,
  },
  {
    id: 3,
    folderId: 11,
    storeId: 1002804,
    permissions: [DocumentPermission.View, DocumentPermission.Upload, DocumentPermission.Download, DocumentPermission.Edit, DocumentPermission.Delete],
    createdAt: '2024-03-01T10:00:00Z',
    createdByUserId: 2,
  },
  {
    id: 4,
    folderId: 21,
    storeId: 2,
    permissions: [DocumentPermission.View, DocumentPermission.Upload, DocumentPermission.Download, DocumentPermission.Edit, DocumentPermission.Delete],
    createdAt: '2024-03-05T10:00:00Z',
    createdByUserId: 4,
  },
  {
    id: 5,
    folderId: 26,
    storeId: 3,
    permissions: [DocumentPermission.View, DocumentPermission.Upload, DocumentPermission.Download, DocumentPermission.Edit, DocumentPermission.Delete],
    createdAt: '2024-03-07T10:00:00Z',
    createdByUserId: 6,
  },
];

// Mutable ID generator object for use by clients
export const idGenerators = {
  nextFolderId: 300,
  nextDocumentId: 100,
  nextVersionId: 200,
  nextPermissionId: 10,
};
